import { IconInfoCircle } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { TooltipHoverTarget } from "./hoverTarget.js";
import { SimpleTooltip } from "./simpleTooltip.js";
import { TooltipTrigger } from "./tooltipTrigger.js";

export interface InfoIconTooltipProps {
  /** The explanation to show on hover or focus. */
  children?: ReactNode;
  /**
   * Applied to the hover target, so callers can control layout and reveal the
   * icon on hover of an ancestor (e.g. `opacity-0 group-hover:opacity-100`).
   */
  className?: string;
}

/**
 * Info-circle icon that explains an adjacent label on hover.
 *
 * For an explanation that is long enough to want reading rather than glancing
 * at, or one containing links, use `InfoButton` with a dialog instead.
 */
export const InfoIconTooltip: FC<InfoIconTooltipProps> = ({
  children,
  className,
}) => (
  <TooltipTrigger
    // The hover target below presents as a button (role="button", darkens on
    // hover) yet has nothing to press, and React Aria closes a tooltip when its
    // trigger is pressed. Keep it open through the press, so clicking the icon
    // doesn't dismiss the help text while the pointer is still on it.
    shouldCloseOnPress={false}
  >
    <TooltipHoverTarget
      className={twMerge("shrink-0", className)}
      shape="rounded"
    >
      <IconInfoCircle
        aria-hidden="true"
        className={twMerge(
          "size-4 text-gray-400 transition-colors hover:text-gray-600",
        )}
      />
    </TooltipHoverTarget>
    <SimpleTooltip>{children}</SimpleTooltip>
  </TooltipTrigger>
);
